import { lazy } from 'react'

import {
  PagesFolderInformationDocument,
  PagesPageHeaderInfoDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import {
  NavigationRoutesFn,
  buildModuleRoute,
  buildNavigation,
} from 'modules/core/lib/react-router'

type Routes = ReturnType<NavigationRoutesFn>
type Navigation = Parameters<NavigationRoutesFn>[0]

const RootPage = lazy(() => import('modules/pages/pages/RootPage'))
const FoldersOverview = lazy(() => import('modules/pages/pages/FoldersOverview'))
const Folder = lazy(() => import('modules/pages/pages/Folder'))
const FolderHeader = lazy(() => import('modules/pages/pages/Folder/components/FolderHeader'))
const Page = lazy(() => import('modules/pages/pages/Page'))

export const pagesRoutes = buildNavigation((navigation) => {
  return [
    buildModuleRoute({
      path: navigation.pages.path,
      element: <RootPage />,
      handle: {
        module: 'pages',
        moduleName: () => i18n.t('sidebar.policies'),
        meta: () => {
          return { title: i18n.t('sidebar.policies') }
        },
      },

      children: [...allPages(navigation)],
    }),
  ]
})

function allPages(navigation: Navigation): Routes {
  return [
    {
      index: true,
      element: <FoldersOverview />,
    },
    {
      path: navigation.pages.folder.path,
      handle: {
        resource: true,
        meta: ({ params: { folderSlug }, watchQuery }) => {
          return watchQuery({
            query: PagesFolderInformationDocument,
            variables: {
              slug: String(folderSlug),
            },
          }).map(({ data }) => {
            const folder = data?.handbookPages.foldersConnection.nodes[0]
            return {
              title: folder?.name,
              headerComponent: (
                <FolderHeader
                  title={folder?.name}
                  description={folder?.description}
                  slug={folder?.slug}
                />
              ),
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <Folder />,
        },
        {
          path: navigation.pages.folder.page.path,
          element: <Page />,
          handle: {
            resource: true,
            meta: ({ params: { pageId, folderSlug }, watchQuery, navigate }) => {
              return watchQuery({
                query: PagesPageHeaderInfoDocument,
                variables: {
                  id: Number(pageId),
                },
              }).map(({ data }) => {
                const page = data?.handbookPages.page
                // if the page is in a different folder, navigate to the page in the correct folder
                if (page?.folderType && page.folderType !== folderSlug) {
                  navigate(
                    navigation.pages.folder.page.fullPath({
                      folderSlug: page.folderType,
                      pageId: Number(pageId),
                    })
                  )
                }
                return {
                  title: page?.title,
                  headerComponent: null,
                }
              })
            },
          },
        },
      ],
    },
  ]
}
